import type { PrintSectionStyle, PrintTemplate } from "@repo/types";
import { DEFAULT_ARBRE_TEMPLATE } from "./default-template";

/** Déplace un élément d'un index à un autre (copie du tableau). */
export function arrayMove<T>(list: T[], from: number, to: number): T[] {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= list.length ||
    to >= list.length
  )
    return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}

export type TemplateEditAction =
  | { type: "reset"; to?: PrintTemplate }
  | {
      type: "toggleHeader";
      key: "showLogo" | "showOrganisation" | "showSiret" | "showRecenseur";
    }
  | { type: "setHeaderColor"; key: "titleBg" | "titleColor"; value?: string }
  | { type: "toggleSection"; id: string }
  | {
      type: "setSectionStyle";
      id: string;
      key: keyof PrintSectionStyle;
      value?: string;
    }
  | { type: "setSectionTitle"; id: string; title: string }
  | { type: "setSectionColumns"; id: string; columns: number }
  | { type: "moveSection"; activeId: string; overId: string }
  | { type: "toggleItem"; sectionId: string; field: string }
  | { type: "toggleHideIfEmpty"; sectionId: string; field: string }
  | { type: "setItemLabel"; sectionId: string; field: string; label: string }
  | { type: "setItemCol"; sectionId: string; field: string; col: number }
  | {
      type: "moveItem";
      sectionId: string;
      activeField: string;
      overField: string;
    }
  /** Glisser un champ vers une colonne : devant `overField` s'il est visé, sinon en fin de colonne. */
  | {
      type: "moveItemTo";
      sectionId: string;
      field: string;
      toCol: number;
      overField?: string;
    };

type Section = PrintTemplate["sections"][number];
type Item = Section["items"][number];

const mapSection = (
  t: PrintTemplate,
  id: string,
  fn: (s: Section) => Section,
): PrintTemplate => {
  let changed = false;
  const sections = t.sections.map((s) => {
    if (s.id !== id) return s;
    const next = fn(s);
    if (next !== s) changed = true;
    return next;
  });
  return changed ? { ...t, sections } : t;
};

const mapItem = (
  t: PrintTemplate,
  sectionId: string,
  field: string,
  fn: (i: Item) => Item,
) =>
  mapSection(t, sectionId, (s) => ({
    ...s,
    items: s.items.map((i) => (i.field === field ? fn(i) : i)),
  }));

/** Vrai si chaque colonne contient les mêmes champs dans le même ordre (la position dans le tableau global n'a pas d'importance). */
function sameLayout(a: Item[], b: Item[], columns: number): boolean {
  for (let col = 1; col <= columns; col++) {
    const left = a.filter((i) => i.col === col).map((i) => i.field);
    const right = b.filter((i) => i.col === col).map((i) => i.field);
    if (
      left.length !== right.length ||
      left.some((f, index) => f !== right[index])
    )
      return false;
  }
  return true;
}

/** Applique une action d'édition au modèle (fonction pure : renvoie le même objet si rien ne change). */
export function reduceTemplate(
  state: PrintTemplate,
  action: TemplateEditAction,
): PrintTemplate {
  switch (action.type) {
    case "reset":
      return action.to ?? DEFAULT_ARBRE_TEMPLATE;
    case "toggleHeader":
      return {
        ...state,
        header: { ...state.header, [action.key]: !state.header[action.key] },
      };
    case "setHeaderColor":
      return {
        ...state,
        header: { ...state.header, [action.key]: action.value },
      };
    case "setSectionStyle":
      return mapSection(state, action.id, (s) => {
        const style = { ...s.style, [action.key]: action.value };
        if (!action.value) delete style[action.key];
        return {
          ...s,
          style: Object.keys(style).length > 0 ? style : undefined,
        };
      });
    case "toggleSection":
      return mapSection(state, action.id, (s) => ({
        ...s,
        visible: !s.visible,
      }));
    case "setSectionTitle":
      return mapSection(state, action.id, (s) => ({
        ...s,
        title: action.title || undefined,
      }));
    case "setSectionColumns":
      // Les champs placés dans une colonne qui disparaît sont ramenés dans la dernière.
      return mapSection(state, action.id, (s) => ({
        ...s,
        columns: action.columns,
        items: s.items.map((i) => ({
          ...i,
          col: Math.min(i.col, action.columns),
        })),
      }));
    case "moveSection": {
      const from = state.sections.findIndex((s) => s.id === action.activeId);
      const to = state.sections.findIndex((s) => s.id === action.overId);
      return from < 0 || to < 0 || from === to
        ? state
        : { ...state, sections: arrayMove(state.sections, from, to) };
    }
    case "toggleItem":
      return mapItem(state, action.sectionId, action.field, (i) => ({
        ...i,
        visible: !i.visible,
      }));
    case "toggleHideIfEmpty":
      return mapItem(state, action.sectionId, action.field, (i) => ({
        ...i,
        hideIfEmpty: !i.hideIfEmpty,
      }));
    case "setItemLabel":
      return mapItem(state, action.sectionId, action.field, (i) => ({
        ...i,
        label: action.label || undefined,
      }));
    case "setItemCol":
      return mapItem(state, action.sectionId, action.field, (i) => ({
        ...i,
        col: action.col,
      }));
    case "moveItem":
      return mapSection(state, action.sectionId, (s) => {
        const from = s.items.findIndex((i) => i.field === action.activeField);
        const to = s.items.findIndex((i) => i.field === action.overField);
        return from < 0 || to < 0 || from === to
          ? s
          : { ...s, items: arrayMove(s.items, from, to) };
      });
    case "moveItemTo":
      return mapSection(state, action.sectionId, (s) => {
        const from = s.items.findIndex((i) => i.field === action.field);
        if (from < 0 || action.field === action.overField) return s;
        const current = s.items[from] as Item;
        const toCol = Math.min(Math.max(action.toCol, 1), s.columns);
        const items = s.items.map((i) =>
          i === current && i.col !== toCol ? { ...i, col: toCol } : i,
        );
        const over = action.overField
          ? items.findIndex((i) => i.field === action.overField)
          : -1;
        // L'ordre dans une colonne est celui du tableau. Dans la même colonne, on suit la liste (glisser vers le bas =
        // passer après le champ visé) ; vers une autre colonne, le champ se place devant celui visé, ou en fin de colonne.
        let to = items.length - 1;
        if (over >= 0) {
          to = current.col === toCol || from > over ? over : over - 1;
        }
        const next = arrayMove(items, from, to);
        return sameLayout(s.items, next, s.columns) ? s : { ...s, items: next };
      });
  }
}

// ─────────────────────────── Historique (annuler / rétablir) ───────────────────────────

export interface TemplateHistory {
  past: PrintTemplate[];
  present: PrintTemplate;
  future: PrintTemplate[];
  /** Clé et heure de la dernière modification de texte ou de couleur : les frappes rapprochées ne font qu'une étape. */
  lastKey?: string;
  lastAt?: number;
}

export type TemplateHistoryAction = (
  | { type: "undo" }
  | { type: "redo" }
  | TemplateEditAction
) & { at?: number };

export const HISTORY_LIMIT = 100;
export const COALESCE_MS = 1000;

export const createHistory = (present: PrintTemplate): TemplateHistory => ({
  past: [],
  present,
  future: [],
});

/** Les saisies continues (texte, sélecteur de couleur) sont regroupées : une frappe ne crée pas une étape d'historique. */
const coalesceKey = (action: TemplateEditAction): string | undefined => {
  switch (action.type) {
    case "setSectionTitle":
      return `title:${action.id}`;
    case "setItemLabel":
      return `label:${action.sectionId}:${action.field}`;
    case "setSectionStyle":
      return `style:${action.id}:${action.key}`;
    case "setHeaderColor":
      return `header:${action.key}`;
    default:
      return undefined;
  }
};

export function reduceHistory(
  history: TemplateHistory,
  action: TemplateHistoryAction,
): TemplateHistory {
  if (action.type === "undo") {
    const previous = history.past.at(-1);
    if (!previous) return history;
    return {
      past: history.past.slice(0, -1),
      present: previous,
      future: [history.present, ...history.future],
    };
  }
  if (action.type === "redo") {
    const [next, ...rest] = history.future;
    if (!next) return history;
    return {
      past: [...history.past, history.present],
      present: next,
      future: rest,
    };
  }

  const present = reduceTemplate(history.present, action);
  if (present === history.present) return history;

  const key = coalesceKey(action);
  const at = action.at;
  const merge =
    key !== undefined &&
    at !== undefined &&
    key === history.lastKey &&
    history.lastAt !== undefined &&
    at - history.lastAt < COALESCE_MS;
  return {
    past: merge
      ? history.past
      : [...history.past, history.present].slice(-HISTORY_LIMIT),
    present,
    future: [],
    lastKey: key,
    lastAt: key ? at : undefined,
  };
}
